import * as Sentry from '@sentry/react'
import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import './index.css'

const CHUNK_RELOAD_FLAG = 'chunk_reload_recovery_attempted'
const CHUNK_ERROR_PATTERNS = [
  'importing a module script failed',
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'loading chunk',
  'chunkloaderror',
]
const BENIGN_SENTRY_PATTERNS = [
  'failed to fetch assessment structure',
]
const isWebDriverRun = typeof navigator !== 'undefined' && navigator.webdriver === true
const shouldEnableChunkRecovery = !isWebDriverRun

const isBenignNetworkTransitionError = (value: unknown): boolean => {
  const message =
    typeof value === 'string'
      ? value
      : typeof (value as { message?: unknown } | null)?.message === 'string'
        ? String((value as { message?: unknown }).message)
        : ''
  if (!message) return false
  const normalized = message.toLowerCase()
  if (BENIGN_SENTRY_PATTERNS.some((pattern) => normalized.includes(pattern))) return true
  if (
    normalized.includes('load failed')
    && (normalized.includes('readiness-api.seargin.com') || normalized.includes('assessment structure'))
  ) {
    return true
  }
  return false
}

const isChunkImportFailure = (value: unknown): boolean => {
  const message =
    typeof value === 'string'
      ? value
      : typeof (value as { message?: unknown } | null)?.message === 'string'
        ? String((value as { message?: unknown }).message)
        : ''
  if (!message) return false
  const normalized = message.toLowerCase()
  return CHUNK_ERROR_PATTERNS.some((pattern) => normalized.includes(pattern))
}

const attemptChunkRecoveryReload = (): boolean => {
  if (window.sessionStorage.getItem(CHUNK_RELOAD_FLAG) === '1') {
    return false
  }
  window.sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1')
  const url = new URL(window.location.href)
  url.searchParams.set('__chunk_retry', String(Date.now()))
  window.location.replace(url.toString())
  return true
}

if (shouldEnableChunkRecovery) {
  window.addEventListener('vite:preloadError', (event) => {
    const payload = (event as Event & { payload?: unknown }).payload
    if (!isChunkImportFailure(payload)) return
    event.preventDefault()
    void attemptChunkRecoveryReload()
  })

  window.addEventListener('error', (event) => {
    if (!isChunkImportFailure(event.error ?? event.message)) return
    void attemptChunkRecoveryReload()
  })

  window.addEventListener('unhandledrejection', (event) => {
    if (!isChunkImportFailure(event.reason)) return
    void attemptChunkRecoveryReload()
  })
}

if (window.location.search.includes('__chunk_retry=')) {
  const url = new URL(window.location.href)
  url.searchParams.delete('__chunk_retry')
  window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`)
}

window.setTimeout(() => {
  window.sessionStorage.removeItem(CHUNK_RELOAD_FLAG)
}, 4000)

const storedTheme = window.localStorage.getItem('ui_theme')
const preAppliedTheme = document.documentElement.dataset.theme
const initialTheme =
  preAppliedTheme === 'light' || preAppliedTheme === 'dark'
    ? preAppliedTheme
    : storedTheme === 'light' || storedTheme === 'dark'
      ? storedTheme
      : window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'

if (initialTheme === 'dark') {
  document.documentElement.classList.add('dark')
  document.documentElement.dataset.theme = 'dark'
} else {
  document.documentElement.dataset.theme = 'light'
}

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  // Only report from production builds — local `npm run dev` errors stay in the
  // browser console and never reach Sentry.
  enabled: import.meta.env.PROD,
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 0.2,
  environment: import.meta.env.MODE,
  release: import.meta.env.VITE_SENTRY_RELEASE || import.meta.env.VITE_COMMIT_SHA || undefined,
  attachStacktrace: true,
  beforeSend(event, hint) {
    const original = hint.originalException
    const eventMessage = event.message || event.exception?.values?.[0]?.value || ''
    if (isChunkImportFailure(original) || isChunkImportFailure(eventMessage)) {
      return null
    }
    if (isBenignNetworkTransitionError(original) || isBenignNetworkTransitionError(eventMessage)) {
      return null
    }
    return event
  },
})

const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : ''
const normalizedUA = userAgent.toLowerCase()
const isMobileClient = /android|iphone|ipad|ipod|mobile|windows phone|webos/i.test(normalizedUA)
const isInAppBrowser = /(fbav|fban|instagram|line|micromessenger|wv)/i.test(normalizedUA)
Sentry.setTag('device.class', isMobileClient ? 'mobile' : 'desktop')
Sentry.setTag('client.in_app_browser', isInAppBrowser ? 'true' : 'false')
Sentry.setContext('client_runtime', {
  userAgent,
  deviceClass: isMobileClient ? 'mobile' : 'desktop',
  inAppBrowser: isInAppBrowser,
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <Sentry.ErrorBoundary fallback={<span style={{ display: 'none' }} />}>
    <App />
  </Sentry.ErrorBoundary>,
)

const bootLoader = document.getElementById('boot-loader')
if (bootLoader) {
  const isResultsFromAdmin =
    window.location.pathname.startsWith('/results/') &&
    new URLSearchParams(window.location.search).get('fromAdmin') === '1'

  if (isResultsFromAdmin) {
    // Results page will hide this loader when data is actually ready.
  } else {
    let removed = false
    let sawGlobalLoading = false
    const removeBootLoader = () => {
      if (removed) return
      removed = true
      bootLoader.style.opacity = '0'
      bootLoader.style.pointerEvents = 'none'
      window.setTimeout(() => {
        bootLoader.remove()
      }, 170)
      window.removeEventListener('global-loading-visible', onGlobalLoadingVisible as EventListener)
      window.removeEventListener('global-loading-hidden', onGlobalLoadingHidden as EventListener)
    }

    const onGlobalLoadingVisible = () => {
      sawGlobalLoading = true
    }

    const onGlobalLoadingHidden = () => {
      if (sawGlobalLoading) {
        removeBootLoader()
      }
    }

    // Keep boot loader on top while global loader is active to avoid spinner phase reset flicker.
    window.addEventListener('global-loading-visible', onGlobalLoadingVisible as EventListener)
    window.addEventListener('global-loading-hidden', onGlobalLoadingHidden as EventListener)

    // Safety fallback if no global loading is triggered at all.
    window.setTimeout(() => {
      if (!sawGlobalLoading) {
        removeBootLoader()
      }
    }, 1200)

    // Hard safety net: never let boot loader block interactions indefinitely.
    window.setTimeout(() => {
      removeBootLoader()
    }, 8000)
  }
}
